/**
 * Browser transports for {@link EventQueue.flush}.
 *
 * - {@link createFetchSend}: `fetch` POST. `keepalive` is set ONLY when the body
 *   is under 60 KiB — browsers cap the total in-flight keepalive body at 64 KiB
 *   and reject (not truncate) anything larger, which used to lose whole batches.
 * - {@link createBeaconSend}: `navigator.sendBeacon` for `pagehide` /
 *   `visibilitychange`, falling back to the fetch transport.
 */

import { byteLength } from "../queue";
import type { SendFn } from "../types";

/** Bodies must be strictly smaller than this to use `keepalive` / `sendBeacon`. */
export const KEEPALIVE_LIMIT_BYTES = 60 * 1024;

export interface FetchSendOptions {
  endpoint: string;
  credentials?: RequestCredentials;
  /** Override for tests / non-global fetch. */
  fetchImpl?: typeof fetch;
}

export function createFetchSend(options: FetchSendOptions): SendFn {
  return async (body) => {
    const doFetch = options.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : null);
    if (!doFetch) return { ok: false };
    const keepalive = byteLength(body) < KEEPALIVE_LIMIT_BYTES;
    try {
      const response = await doFetch(options.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        ...(keepalive ? { keepalive: true } : {}),
        credentials: options.credentials ?? "same-origin",
      });
      const status = response.status;
      return status >= 200 && status < 300 ? { ok: true } : { ok: false, status };
    } catch {
      return { ok: false };
    }
  };
}

export interface BeaconSendOptions {
  endpoint: string;
  /** Used when `sendBeacon` is missing, refuses the payload, or the body is too large. */
  fallback: SendFn;
}

export function createBeaconSend(options: BeaconSendOptions): SendFn {
  return async (body) => {
    const nav = typeof navigator !== "undefined" ? navigator : undefined;
    if (nav && typeof nav.sendBeacon === "function" && byteLength(body) < KEEPALIVE_LIMIT_BYTES) {
      try {
        if (nav.sendBeacon(options.endpoint, new Blob([body], { type: "application/json" }))) {
          return { ok: true };
        }
      } catch {
        // fall through to the fetch fallback
      }
    }
    return options.fallback(body);
  };
}
